import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Form, Input, Button, message } from 'antd'
import { useAuth } from '../stores/auth'
import { tokens } from '../theme/tokens'
import ThemeModeSelect from '../theme/ThemeModeSelect'
import { consumeSessionExpiredNotice } from '../auth/sessionExpiry'
import { runLoginSubmission } from '../utils/loginSubmission'
import './Auth.css'

export default function Login() {
  const { login, loading } = useAuth()
  const navigate = useNavigate()
  const [msg, contextHolder] = message.useMessage()
  const submitting = useRef(false)

  useEffect(() => {
    if (consumeSessionExpiredNotice()) {
      msg.error({ key: 'session-expired', content: '登录已过期, 请重新登录' })
    }
  }, [msg])

  const onFinish = (values: { username: string; password: string }) =>
    runLoginSubmission(submitting, async () => {
      try {
        await login(values.username, values.password)
        navigate('/', { replace: true })
      } catch (e: any) {
        msg.error(e.message || '登录失败')
      }
    })

  return (
    <div className="login-page" style={{
      display: 'flex', minHeight: '100vh', background: tokens.colors.background,
    }}>
      {contextHolder}

      {/* Left — decorative brand panel with .AT icon logo */}
      <div className="login-brand-panel" style={{
        flex: '0 0 440px', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', position: 'relative',
        background: tokens.gradients.login,
        overflow: 'hidden',
      }}>
        {/* Soft blur gradient orbs */}
        <div style={{
          position: 'absolute', width: 300, height: 300, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(79,70,229,0.25), transparent 70%)',
          filter: 'blur(40px)', top: -60, left: -40,
        }} />
        <div style={{
          position: 'absolute', width: 250, height: 250, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.2), transparent 70%)',
          filter: 'blur(40px)', bottom: 40, right: -30,
        }} />
        <div style={{
          position: 'absolute', width: 180, height: 180, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.15), transparent 70%)',
          filter: 'blur(30px)', top: '45%', left: '55%',
        }} />

        {/* Subtle geometric rings */}
        <div style={{
          position: 'absolute', width: 320, height: 320, borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.06)',
          top: -80, left: -60,
        }} />
        <div style={{
          position: 'absolute', width: 200, height: 200, borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.04)',
          bottom: 60, right: -40,
        }} />

        <img src="/logo.png" alt="ApexTribe" style={{
          height: 216, objectFit: 'contain', marginBottom: 32,
          filter: 'brightness(0) invert(1)', opacity: 0.95,
        }} />
        <div style={{
          color: 'rgba(255,255,255,0.7)', fontSize: 14, letterSpacing: 3,
          textTransform: 'uppercase', fontWeight: 500,
        }}>
          Operations Management
        </div>
        <div style={{
          color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 8,
          letterSpacing: 1,
        }}>
          Meta Advertising Agency Platform
        </div>
      </div>

      {/* Right — login form with full logo */}
      <div className="login-form-panel" style={{
        flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        <div className="login-form-shell" style={{ width: 380 }}>
          {/* Welcome heading */}
          <div style={{ marginBottom: 48, textAlign: 'center' }}>
            <div style={{
              fontSize: 14, fontWeight: 500, letterSpacing: 4,
              textTransform: 'uppercase', color: tokens.colors.textMuted,
              marginBottom: 8,
            }}>
              Welcome
            </div>
            <div style={{
              fontSize: 36, fontWeight: 800, letterSpacing: 2,
              background: tokens.gradients.heading,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              ATWork
            </div>
          </div>

          <Form onFinish={onFinish} size="large" layout="vertical">
            <Form.Item
              name="username"
              rules={[{ required: true, message: '请输入账号' }]}
            >
              <Input
                placeholder="请输入您的账号"
                style={{
                  height: 48, borderRadius: 10, border: 'none',
                  background: tokens.colors.borderLight, fontSize: 14, paddingLeft: 16,
                }}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                placeholder="请输入您的密码"
                style={{
                  height: 48, borderRadius: 10, border: 'none',
                  background: tokens.colors.borderLight, fontSize: 14, paddingLeft: 16,
                }}
              />
            </Form.Item>
            <Form.Item style={{ marginTop: 8 }}>
              <Button
                type="primary" htmlType="submit" loading={loading} block
                style={{
                  height: 48, borderRadius: 10, fontWeight: 600, fontSize: 15,
                  background: tokens.gradients.primary,
                  border: 'none',
                  boxShadow: tokens.shadows.button,
                  transition: tokens.transition.default,
                }}
              >
                登录
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Link to="/forgot-password" style={{
              fontSize: 13, color: tokens.colors.textMuted,
              textDecoration: 'none',
            }}>
              忘记密码？
            </Link>
          </div>

          <div style={{
            textAlign: 'center', marginTop: 32,
            color: tokens.colors.textPlaceholder, fontSize: 12,
          }}>
            ApexTribe &copy; {new Date().getFullYear()}
          </div>
        </div>
      </div>

      <div className="auth-theme-select"><ThemeModeSelect /></div>
      <style>{`
        .login-page .ant-input, .login-page .ant-input-affix-wrapper {
          background: ${tokens.colors.borderLight} !important;
          border: none !important;
          border-radius: 10px !important;
        }
        .login-page .ant-input-affix-wrapper { padding-right: 14px !important; }
        .login-page .ant-input:focus, .login-page .ant-input-affix-wrapper-focused {
          box-shadow: ${tokens.shadows.input} !important;
        }
        .login-page .ant-btn-primary:hover {
          background: ${tokens.gradients.primary} !important;
          box-shadow: ${tokens.shadows.buttonHover} !important;
          transform: translateY(-1px);
          filter: brightness(1.05);
        }
        .login-page .ant-input-password-icon { color: ${tokens.colors.textMuted} !important; }
        .login-page .ant-form-item-label > label { height: auto !important; }
        @media (max-width: 900px) {
          .login-brand-panel { display: none !important; }
        }
      `}</style>
    </div>
  )
}
